<template>
  <div class="terms-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Terms &amp; Conditions Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportItems.length }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close-btn" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
      <p class="mt-3 font-weight-bold">Loading app report data...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3"
        >Retry</b-button
      >
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text"
                >Terms &amp; Conditions Report — Student App Status</span
              >
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters Summary -->
      <div class="filters-summary mb-3" v-if="filterSummary">
        <span class="filter-label">Filters Applied:</span>
        <span class="filter-value">{{ filterSummary }}</span>
      </div>

      <!-- Summary Section -->
      <div class="summary-section mb-3">
        <div class="summary-card">
          <span class="label">Total Records:</span>
          <span class="value text-primary">{{ reportItems.length }}</span>
        </div>
        <div class="summary-card">
          <span class="label">Agreed:</span>
          <span class="value text-success">{{ agreedCount }}</span>
        </div>
        <div class="summary-card">
          <span class="label">Pending:</span>
          <span class="value text-warning">{{ pendingCount }}</span>
        </div>
      </div>

      <!-- Data Table -->
      <div class="table-container" v-if="reportItems.length > 0">
        <table class="ledger-style-table">
          <thead>
            <tr class="main-header">
              <th style="width: 5%">#</th>
              <th style="width: 20%">Student Name</th>
              <th style="width: 15%">Father Name</th>
              <th style="width: 15%">Class</th>
              <th style="width: 15%">Contact</th>
              <th style="width: 15%">Agreed Date</th>
              <th style="width: 15%">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in reportItems"
              :key="index"
              :class="index % 2 === 0 ? 'row-even' : 'row-odd'"
            >
              <td class="serial-cell">{{ index + 1 }}</td>
              <td class="name-cell">
                {{ item.studentName || item.name || "-" }}
              </td>
              <td class="father-cell">
                {{ item.father_name || item.fatherName || item.father || "-" }}
              </td>
              <td class="class-cell">
                {{ item.className || item.class || "-" }}
              </td>
              <td class="contact-cell">
                {{
                  item.contact_father ||
                  item.contact ||
                  item.phone ||
                  item.mobile ||
                  "-"
                }}
              </td>
              <td class="date-cell">
                {{ formatDate(item.agreedDate || item.createdOn || item.date) }}
              </td>
              <td class="status-cell">
                <span class="status-badge" :class="getStatusClass(item.status)">
                  {{ item.status || "Pending" }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- No Data -->
      <div v-else class="text-center py-5 text-muted">
        <feather-icon icon="AlertCircleIcon" size="48" class="mb-3" />
        <p>No data available for the selected filters.</p>
      </div>

      <!-- Footer -->
      <div class="page-footer">
        <small
          >Powered by MySkool.app | Generated On: {{ currentDateTime }}</small
        >
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "TermsHistoryPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      schoolLogo: null,
      campusName: "",
      campusBranch: "",
      db: "",
      cID: "",
      classID: "",
      fromDate: "",
      toDate: "",
      status: "",
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
    };
  },
  computed: {
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    agreedCount() {
      return this.reportItems.filter(
        (i) => (i.status || "").toLowerCase() === "agreed"
      ).length;
    },
    pendingCount() {
      return this.reportItems.filter(
        (i) => (i.status || "").toLowerCase() !== "agreed"
      ).length;
    },
    filterSummary() {
      const parts = [];
      if (this.fromDate) parts.push(`From: ${this.fromDate}`);
      if (this.toDate) parts.push(`To: ${this.toDate}`);
      if (this.classID && this.classID != 0)
        parts.push(`Class ID: ${this.classID}`);
      if (this.status) parts.push(`Status: ${this.status}`);
      return parts.join(" | ");
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db;
      this.cID = query.cID || query.cId || 0;
      this.classID = query.classID || 0;
      this.fromDate = query.fromDate || "";
      this.toDate = query.toDate || "";
      this.status = query.status || "";

      this.campusName =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.branch) ||
        "My Skool";
      this.schoolLogo =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.logo) ||
        null;

      if (!this.db) {
        this.error = "Invalid parameters. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: {
            db: this.db,
            cId: this.cID,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          response.data.data &&
          response.data.data.length > 0
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          const info = match || response.data.data[0];
          this.campusName = info.name;
          this.campusBranch = this.cID == 0 ? "All Campuses" : info.branch;
          this.schoolLogo = info.logo;
        }
      } catch (e) {
        console.error("Failed to load campus info:", e);
      }
    },
    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;

        let baseUrl = this.$store.state.domain;
        if (!baseUrl.endsWith("/")) baseUrl += "/";

        const url = `${baseUrl}/Terms/GetTermsHistoryReport`;

        const params = {
          db: this.db,
          campusID: this.cID,
          type: "Student",
        };

        if (this.classID && this.classID != 0) params.classID = this.classID;
        if (this.fromDate) params.fromDate = this.fromDate;
        if (this.toDate) params.toDate = this.toDate;
        if (this.status) params.status = this.status;

        const response = await axios.get(url, {
          params,
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (response.data && response.data.status === "success") {
          this.reportItems = response.data.data || [];
        } else {
          this.error = response.data?.message || "Failed to load report data.";
        }
      } catch (error) {
        console.error("Failed to load terms history report:", error);
        this.error =
          "Network error or server unavailable. Please ensure the API is running.";
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      } catch {
        return dateStr;
      }
    },
    getStatusClass(status) {
      if (!status) return "badge-pending";
      const s = status.toLowerCase();
      if (s === "agreed") return "badge-agreed";
      return "badge-pending";
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.terms-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close-btn {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 90px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 1.7rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 5px;
    }

    .date-range-text {
      color: #555;
    }
  }
}

.filters-summary {
  padding: 8px 14px;
  background: #f8f9fa;
  border-left: 4px solid #6c5ce7;
  border-radius: 4px;
  font-size: 0.88rem;

  .filter-label {
    font-weight: 700;
    color: #444;
    margin-right: 8px;
  }

  .filter-value {
    color: #555;
  }
}

.summary-section {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #eee;

  .summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;

    .label {
      font-size: 0.8rem;
      font-weight: 700;
      color: #636e72;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 4px;
    }

    .value {
      font-size: 1.4rem;
      font-weight: 800;
    }
  }
}

.table-container {
  margin-top: 1.5rem;
  overflow-x: auto;
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 10px 14px;
    box-sizing: border-box;
    text-align: left;
    font-size: 0.9rem;
  }

  thead {
    background: #2c3e50;
    th {
      color: white;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.82rem;
      letter-spacing: 0.5px;
    }
  }

  .row-even {
    background: #ffffff;
  }

  .row-odd {
    background: #f8f9fa;
  }

  tbody tr:hover {
    background: #f0f0ff;
  }

  .serial-cell {
    font-weight: 700;
    color: #636e72;
    text-align: center;
  }

  .name-cell {
    font-weight: 700;
    color: #2c3e50;
  }

  .father-cell {
    color: #444;
  }

  .class-cell {
    font-weight: 600;
    color: #2c3e50;
  }

  .contact-cell {
    color: #555;
    font-family: monospace;
    font-size: 0.88rem;
  }

  .date-cell {
    color: #2c3e50;
    white-space: nowrap;
    font-size: 0.88rem;
  }

  .status-cell {
    text-align: center;
  }
}

.status-badge {
  display: inline-block;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &.badge-agreed {
    background: transparent;
    color: #155724;
  }

  &.badge-pending {
    background: transparent;
    color: #856404;
  }
}

.page-footer {
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid #eee;
  color: #adb5bd;
}

.text-center {
  text-align: center;
}
.font-weight-bold {
  font-weight: 700;
}

@page {
  size: auto;
  margin: 0mm;
}

@media print {
  .no-print {
    display: none !important;
  }

  html,
  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .terms-report {
    background: white !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.5cm 0.4cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    box-sizing: border-box !important;
  }

  .ledger-style-table {
    font-size: 8.5pt !important;
    width: 100% !important;

    th,
    td {
      padding: 6px 10px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .row-even {
      background: #fff !important;
    }

    .row-odd {
      background: #f9f9f9 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .status-badge {
    border: 0 !important;
    border-width: 0 !important;
    border-radius: 0 !important;
    outline: none !important;
    box-shadow: none !important;
    color: #000 !important;
    background: transparent !important;
    font-weight: 800 !important;
    padding: 0 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .summary-section {
    margin: 5px 0 !important;
    padding: 8px !important;
    background: #fdfdfd !important;
    border: 1px solid #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    display: flex !important;
    flex-direction: row !important;
    justify-content: center !important;
    gap: 30px !important;

    .summary-card {
      flex-direction: row !important;
      gap: 8px !important;
      align-items: center !important;

      .label {
        font-size: 0.8rem !important;
        margin-bottom: 0 !important;
        color: #000 !important;
        font-weight: 800 !important;
      }

      .value {
        font-size: 1.1rem !important;
        color: #000 !important;
        font-weight: 800 !important;
      }
    }
  }

  .report-header-modern {
    margin-bottom: 10px !important;
    padding-bottom: 8px !important;
    border-bottom: 3px solid #000 !important;

    .school-logo-img {
      height: 60px !important;
    }

    .school-name-full {
      font-size: 1.4rem !important;
      color: #000 !important;
    }

    .report-info-line {
      font-size: 0.95rem !important;
      color: #000 !important;

      .report-name-text {
        border-right-color: #000 !important;
      }
    }
  }

  .filters-summary {
    border-left: none !important;
    border-bottom: 1px solid #000 !important;
    font-size: 0.82rem !important;
    padding: 4px 0 !important;
    background: transparent !important;
  }

  .page-footer {
    display: none;
  }
}
</style>
